/**
 * Sugerir LÍNEAS DE FACTURACIÓN de una oportunidad desde la propuesta (IA).
 * Mientras es solo una oportunidad (no un proyecto), el copiloto no propone hitos
 * de pago sino las PARTIDAS FACTURABLES de la propuesta. El usuario elige la
 * propuesta (PDF), la IA extrae {concepto, importe}, el usuario asigna a cada
 * partida una línea de servicio del catálogo y, al aplicar, se AÑADEN al editor
 * de líneas de la ficha (no se guardan hasta que el usuario guarda la ficha).
 *
 * Diseño en UNA sola columna: entrada arriba, tabla de partidas abajo a todo el
 * ancho (las columnas apretadas del split-screen anterior quedaban ilegibles).
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../../lib/money'
import Modal from '../ui/Modal'
import { newDraft, type ServiceLineDraft } from './ServiceLineEditor'
import { type DocumentListItem, type SuggestServiceLinesResponse } from '@toga/shared'

const inputCls =
  'w-full px-2 py-1.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] text-sm t-primary placeholder:t-faint focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

interface Row {
  concept: string
  code: string
  amountEur: string
}

interface Props {
  open: boolean
  onClose: () => void
  opportunityId: string
  catalogLines: { code: string; name: string }[]
  /** Añade las líneas revisadas como drafts al editor de la ficha. */
  onApply: (drafts: ServiceLineDraft[]) => void
}

export default function SuggestServiceLinesModal({
  open,
  onClose,
  opportunityId,
  catalogLines,
  onApply,
}: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const defaultCode = catalogLines[0]?.code ?? ''

  const [docs, setDocs] = useState<DocumentListItem[]>([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [documentId, setDocumentId] = useState('')

  const [suggesting, setSuggesting] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiNotConfigured, setAiNotConfigured] = useState(false)
  const [notes, setNotes] = useState<string | null>(null)

  const [rows, setRows] = useState<Row[]>([])

  useEffect(() => {
    if (!open) return
    setDocumentId('')
    setAiError(null)
    setAiNotConfigured(false)
    setNotes(null)
    setRows([])

    let cancelled = false
    setDocsLoading(true)
    api
      .get<{ items: DocumentListItem[] }>(`/documents?opportunityId=${opportunityId}`)
      .then((res) => {
        if (cancelled) return
        const pdfs = res.items.filter((d) => d.latestMimeType === 'application/pdf')
        setDocs(pdfs)
        const only = pdfs[0]
        if (pdfs.length === 1 && only) setDocumentId(only.id)
      })
      .catch((err) => {
        if (cancelled) return
        const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
        setAiError(body?.error ?? (err instanceof Error ? err.message : t('suggestLines.errors.loadDocs')))
      })
      .finally(() => {
        if (!cancelled) setDocsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, opportunityId, t])

  const onSuggest = async () => {
    if (!documentId) return
    setSuggesting(true)
    setAiError(null)
    setAiNotConfigured(false)
    setNotes(null)
    try {
      const res = await api.post<SuggestServiceLinesResponse>(
        `/opportunities/${opportunityId}/suggest-service-lines`,
        { documentId }
      )
      setNotes(res.notes)
      setRows(
        res.lines.map((l) => ({
          concept: l.concept,
          code: defaultCode,
          amountEur: centsToEurosInput(l.amountCents),
        }))
      )
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { code?: string; error?: string }) : undefined
      if (body?.code === 'AI_NOT_CONFIGURED') {
        setAiNotConfigured(true)
      } else {
        setAiError(body?.error ?? (err instanceof Error ? err.message : t('suggestLines.errors.suggest')))
      }
    } finally {
      setSuggesting(false)
    }
  }

  const updateRow = (i: number, patch: Partial<Row>) => {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  }
  const addRow = () => setRows((rs) => [...rs, { concept: '', code: defaultCode, amountEur: '' }])
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, j) => j !== i))

  const sumCents = useMemo(
    () => rows.reduce((acc, r) => acc + parseEurosToCents(r.amountEur), 0),
    [rows]
  )

  const canApply = rows.length > 0 && rows.every((r) => r.code) && catalogLines.length > 0

  const onApplyClick = () => {
    if (!canApply) return
    const drafts: ServiceLineDraft[] = rows.map((r) => {
      const d = newDraft(r.code)
      const cents = parseEurosToCents(r.amountEur)
      d.valueEuros = cents > 0 ? centsToEurosInput(cents) : ''
      d.billingMode = 'FACTURABLE'
      return d
    })
    onApply(drafts)
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} title={t('suggestLines.title')} maxWidth="max-w-2xl">
      <div className="space-y-4">
        {/* Entrada: propuesta + botón IA */}
        <div className="space-y-3">
          <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
            <Sparkles size={15} className="text-toga-700 dark:text-toga-300" />
            {t('suggestLines.proposal')}
          </h3>

          {docsLoading ? (
            <p className="text-xs t-faint flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> {t('suggestLines.loadingDocs')}
            </p>
          ) : docs.length === 0 ? (
            <p className="text-xs t-faint">{t('suggestLines.uploadFirst')}</p>
          ) : (
            <label className="block space-y-1">
              <span className="text-xs t-secondary">{t('suggestLines.proposalPdf')}</span>
              <select value={documentId} onChange={(e) => setDocumentId(e.target.value)} className={inputCls}>
                <option value="">{t('suggestLines.selectProposal')}</option>
                {docs.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.fileName} (v{d.latestVersion})
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!documentId || suggesting}
              onClick={() => void onSuggest()}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {suggesting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {t('suggestLines.suggestWithAi')}
            </button>
            <span className="text-[11px] t-faint">{t('suggestLines.billableOnlyHint')}</span>
          </div>

          {aiNotConfigured && (
            <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
              <AlertCircle size={12} /> {t('suggestLines.aiNotConfigured')}
            </div>
          )}
          {aiError && (
            <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
              <AlertCircle size={12} /> {aiError}
            </div>
          )}
          {notes && (
            <p className="text-xs t-muted bg-[var(--bg-input-soft)] border border-base rounded-lg px-2.5 py-1.5">
              {notes}
            </p>
          )}
        </div>

        {/* Tabla de partidas (a todo el ancho) */}
        {rows.length > 0 && (
          <div className="space-y-3 border-t border-base pt-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="t-primary font-semibold text-sm">{t('suggestLines.suggestedLines')}</h3>
              <button
                type="button"
                onClick={addRow}
                className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
              >
                <Plus size={13} /> {t('suggestLines.add')}
              </button>
            </div>

            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider t-faint">
                <tr>
                  <th className="text-left py-1 font-medium">{t('suggestLines.col.concept')}</th>
                  <th className="text-left py-1 font-medium w-48">{t('suggestLines.col.line')}</th>
                  <th className="text-right py-1 font-medium w-32">{t('suggestLines.col.amount')}</th>
                  <th className="w-7" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-base/40 align-top">
                    <td className="py-1.5 pr-2">
                      <input
                        value={r.concept}
                        onChange={(e) => updateRow(i, { concept: e.target.value })}
                        placeholder={t('suggestLines.conceptPlaceholder')}
                        className={inputCls}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={r.code}
                        onChange={(e) => updateRow(i, { code: e.target.value })}
                        className={inputCls}
                      >
                        {catalogLines.map((l) => (
                          <option key={l.code} value={l.code}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        value={r.amountEur}
                        onChange={(e) => updateRow(i, { amountEur: e.target.value })}
                        placeholder="0,00"
                        className={`${inputCls} text-right font-mono`}
                        inputMode="decimal"
                      />
                    </td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        className="t-faint hover:text-rose-700 dark:hover:text-rose-300"
                        title={t('suggestLines.deleteRow')}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="text-xs t-faint text-right">
              {t('suggestLines.sum')}: <span className="font-mono t-secondary">{formatCents(sumCents)}</span>
            </p>
            <p className="text-[11px] t-muted">{t('suggestLines.applyHint')}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={!canApply}
            onClick={onApplyClick}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('suggestLines.apply')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
